import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { Text } from '@/design-system/atoms/Text';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearchParams } from '@/hooks/useSearchParams';

import { useProviderOutsideMatchesContext } from '../../contexts/ProviderOutsideMatchesContext';
import {
    useProviderSearchContext,
    useProviderSearchZipCityContext,
} from '../../contexts/ProviderSearchContext';
import styles from './ProviderToolbar.module.scss';

/**
 * The Provider Toolbar is a component that displays the total number of results and the search radius.
 */
const ProviderToolbar = ({ analytics }) => {
    const searchParams = useSearchParams();
    const breakpoints = useBreakpoint();
    const {
        coveo: {
            controller: { didYouMean, querySummary, resultList },
        },
        handleApplyCorrection,
        queryCorrected,
    } = useProviderSearchContext();
    const { hasOutsideMatches } = useProviderOutsideMatchesContext();
    const { zipCode, city } = useProviderSearchZipCityContext();
    const resultsText = useRef();
    const [totalResults, setTotalResults] = useState(null);
    const [distance, setDistance] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [hasError, setHasError] = useState(false);
    const [searchResponseId, setSearchResponseId] = useState(null);

    /**
     * Scroll to and focus on the results text element
     */
    const scrollAndFocus = useCallback(() => {
        if (resultsText?.current && !breakpoints?.large) {
            resultsText.current.setAttribute('tabIndex', '0');
            resultsText.current.focus();
            resultsText.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [resultsText, breakpoints]);

    useEffect(() => {
        return querySummary.subscribe(() => {
            const { total, query, hasError } = querySummary.state;

            if (total === 0 && didYouMean?.state?.queryCorrection?.correctedQuery) {
                handleApplyCorrection(true);
            }

            if (hasOutsideMatches) {
                setTotalResults((prevTotal) => (prevTotal !== null ? prevTotal : 0));
            } else {
                setTotalResults(total);
            }

            setHasError(hasError);

            // Remove any additional coveo query parameters from the string if they exist
            const parsedSearchTerm =
                query
                    ?.replace(/@mydistanceinmiles\s*<=\s*\d+/g, '')
                    ?.split('AND')?.[0]
                    ?.trim() || '';
            setSearchTerm(parsedSearchTerm);
        });
    }, [didYouMean, handleApplyCorrection, querySummary, searchParams, hasOutsideMatches]);

    useEffect(() => {
        const searchParamDistance = searchParams.get('distance');

        setDistance(searchParamDistance ?? '');
    }, [searchParams]);

    /**
     * Subscribe to changes in the resultList to manage focus on resultsText when new results are loaded
     * and to update the searchResponseId to detect new searches.
     */
    useEffect(() => {
        return resultList.subscribe(() => {
            const { searchResponseId: newSearchResponseId } = resultList.state;

            if (newSearchResponseId && newSearchResponseId !== searchResponseId) {
                setSearchResponseId(newSearchResponseId);
                scrollAndFocus();
            }
        });
    }, [resultList, searchResponseId, scrollAndFocus]);

    return (
        <section className={styles[`provider-toolbar`]}>
            <div ref={resultsText} onBlur={() => resultsText.current?.removeAttribute('tabIndex')}>
                {totalResults > 0 ? (
                    <>
                        {didYouMean?.state?.queryCorrection?.correctedQuery || queryCorrected ? (
                            <div className={styles['provider-toolbar__additional-content']}>
                                {didYouMean?.state?.queryCorrection?.correctedQuery && (
                                    <>
                                        <Text
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T2}
                                            content="Did you mean?"
                                            className={
                                                styles['provider-toolbar__additional-content-text']
                                            }
                                        />
                                        <Button
                                            buttonStyle={Button.STYLE.BREADCRUMB}
                                            label={didYouMean.state.queryCorrection.correctedQuery}
                                            ariaLabel={`Search for ${didYouMean.state.queryCorrection.correctedQuery}`}
                                            className={
                                                styles[
                                                    'provider-toolbar__additional-content-button'
                                                ]
                                            }
                                            onClick={() => handleApplyCorrection(false)}
                                        />
                                    </>
                                )}
                                {queryCorrected && (
                                    <>
                                        <Text
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T2}
                                            content="Showing results for"
                                            className={
                                                styles['provider-toolbar__additional-content-text']
                                            }
                                        />
                                        <Text
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T2}
                                            content={searchTerm}
                                            className={
                                                styles[
                                                    'provider-toolbar__additional-autocorrect-text'
                                                ]
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        ) : (
                            <></>
                        )}
                        <>
                            <Text
                                tag={Text.TAG.P}
                                variant={Text.VARIANT.T2}
                                content={
                                    <>
                                        {totalResults} results
                                        {searchTerm && (
                                            <span>
                                                {` for `}
                                                <span class="type-t1b">{searchTerm}</span>
                                            </span>
                                        )}
                                        {distance && (
                                            <span>
                                                {` within ${distance} miles`}
                                                {(city || zipCode) && (
                                                    <span>
                                                        {` of `}
                                                        <span class="type-t1b">
                                                            {city || zipCode}
                                                        </span>
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                        {!distance && (city || zipCode) && (
                                            <span>
                                                {` near `}
                                                <span class="type-t1b">{city || zipCode}</span>
                                            </span>
                                        )}
                                    </>
                                }
                            />
                        </>
                    </>
                ) : (
                    !hasError && (
                        <span>
                            0 results{' '}
                            {city || zipCode
                                ? `near ${city || zipCode}`
                                : searchTerm
                                  ? `for ${searchTerm}`
                                  : ''}
                        </span>
                    )
                )}
            </div>
        </section>
    );
};

export default ProviderToolbar;
